import {skipToken} from '@reduxjs/toolkit/query';

import {equals} from 'ramda';

import {
  useEvaluateAuthorConfigurationQuery,
  useEvaluateConfigurationQuery,
  type ConfigurationSelection,
  type EvaluateConfigurationApiArg,
  type ProposedDiscount,
} from '@dms/api/new-vehicle';

import {useDebouncedValue, useDeepCompareMemoize} from 'shared/web';

export const EVALUATION_DEBOUNCE_MS = 250;

type EvaluationArg = EvaluateConfigurationApiArg & {catalogueRevision: string};

// eslint-disable-next-line max-params -- Pre-existing violation
export function useEvaluateSelection(
  selection: ConfigurationSelection,
  catalogueRevision: string | undefined,
  proposedDiscount?: ProposedDiscount,
  mode: 'configure' | 'preview' = 'configure',
  enabled = true,
  modelYearVersion?: number
) {
  const canonicalSelection: ConfigurationSelection = {
    modelYearId: selection.modelYearId,
    trimId: selection.trimId,
    variantId: selection.variantId,
    colourId: selection.colourId,
    upholsteryId: selection.upholsteryId,
    featureIds: selection.featureIds,
    packageIds: selection.packageIds,
  };
  const canonicalArg =
    canonicalSelection.modelYearId &&
    canonicalSelection.trimId &&
    canonicalSelection.variantId &&
    catalogueRevision
      ? {
          catalogueRevision,
          evaluateConfigurationRequestBody: {
            ...canonicalSelection,
            ...(proposedDiscount ? {proposedDiscount} : {}),
          },
        }
      : undefined;
  const stableArg = useDeepCompareMemoize<EvaluationArg | undefined>(canonicalArg);
  const debouncedArg = useDebouncedValue(stableArg, EVALUATION_DEBOUNCE_MS);
  const configuredResult = useEvaluateConfigurationQuery(
    enabled && mode === 'configure' ? (debouncedArg ?? skipToken) : skipToken
  );
  const previewResult = useEvaluateAuthorConfigurationQuery(
    enabled && mode === 'preview' && debouncedArg && modelYearVersion
      ? {
          modelYearId: canonicalSelection.modelYearId,
          evaluateAuthorConfigurationRequestBody: {
            ...debouncedArg.evaluateConfigurationRequestBody,
            version: modelYearVersion,
          },
        }
      : skipToken
  );
  const result = mode === 'preview' ? previewResult : configuredResult;
  const error = enabled ? result.error : undefined;
  const isCurrentArg = enabled && equals(debouncedArg, canonicalArg);
  const currentData = isCurrentArg && !error ? result.currentData : undefined;

  return {
    currentData,
    lastData: enabled && !error ? result.data : undefined,
    isEligible: enabled && Boolean(canonicalArg),
    isFetching: enabled && result.isFetching,
    isSettled: Boolean(currentData) && !result.isFetching && isCurrentArg,
    error,
    refetch: result.refetch,
  };
}
